<template>
  <div class="chessboard__container">
    <div class="blue merida">
      <div ref="board" class="cg-board-wrap"></div>
    </div>
    <!-- TODO: playerColor should be turnColor but it's not available here -->
    <Promote
      ref="promoteModal"
      :playerColor="turnColor()"
      @onPromotion="onPromotion"
    />
  </div>
</template>

<script>
import _ from "lodash";
import { CHESS_SIDE } from "vue-chess-shared/constants";
import Chess from "chess.js";
import { playAudio } from "@/utils/audio-helper";
import { Chessground } from "chessground";
import Promote from "./Promote";

export default {
  components: { Promote },
  name: "Chessboard",

  props: {
    // color of bottom side of the board
    orientation: {
      type: String,
      default: CHESS_SIDE.WHITE,
    },
    // the pgn to set to the chessboard
    pgn: {
      type: String,
      default: "",
    },
    // the initial position
    initialFen: {
      type: String,
      default: "",
    },
    movable: {
      type: String,
      default: "",
    },
    // to show possible attack moves from current position
    showThreats: {
      type: Boolean,
      default: false,
    },
    // to enable/disable premove
    premove: {
      type: Boolean,
      default: false,
    },
    // auto promote pawn to queen
    autoQueen: {
      type: Boolean,
      default: false,
    },
    // to enable/disable audio
    audio: {
      type: Boolean,
      default: true,
    },
    coordinates: {
      type: Boolean,
      default: true,
    },
  },

  watch: {
    orientation(orientation) {
      this.ui.set({ orientation: orientation });
      setTimeout(() => {
        this.triggerRerender();
      }, 500);
    },

    pgn(newVal, oldVal) {
      if (newVal === oldVal) return;

      this.loadPosition();
    },

    initialFen(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }

      this.loadPosition();
    },

    movable() {
      this.ui.set({
        movable: {
          color: this.isGameOver() ? "" : this.movable,
        },
      });
    },

    showThreats() {
      if (this.showThreats) {
        this.paintThreats();
      } else {
        this.clearThreats();
      }
    },

    premove() {
      this.ui.set({
        premovable: {
          enabled: this.premove,
        },
      });
    },
  },

  data() {
    return {
      premoveData: null,
      config: {
        fen: this.initialFen,
        highlight: {
          lastMove: true,
          check: true,
        },
        movable: {
          color: this.movable ?? "",
          free: false,
          events: {
            after: (orig, dest) => {
              this.makeMove(orig, dest);
            },
          },
        },
        premovable: {
          enabled: false,
          showDests: true,
          castle: true,
          events: {
            set: (orig, dest) => {
              this.premoveData = { orig: orig, dest: dest };
            },
            unset: () => {
              this.premoveData = null;
            },
          },
        },
        drawable: {
          eraseOnClick: false,
        },
        coordinates: this.coordinates,
      },
    };
  },

  methods: {
    triggerRerender() {
      // dynamic size like 100% cause the board to be unmovable
      // this will make sure chessboard do a refresh render before playing
      document.body.dispatchEvent(new Event("chessground.resize"));
    },

    loadPosition() {
      // console.debug("try to load position");

      if (this.pgn) {
        // console.debug("load by pgn");
        // FIXME: temp fix by replacing the first full Move index with 1. so that game.js can load pgn
        this.engine.load_pgn(this.pgn.replace(/\n\n\d+\.\s/, "\n1. "));
        // this.engine.load_pgn(this.pgn);
      } else {
        // console.debug("load initial FEN:", this.initialFen);
        this.engine.load(this.initialFen);
      }

      this.updateGameUi();
      this.playSound();
    },

    calculatePromotions() {
      const moves = this.engine.moves({ verbose: true });
      this.promotions = [];
      for (const move of moves) {
        if (move.promotion) {
          this.promotions.push(move);
        }
      }
    },

    countThreats(color) {
      const threats = {};
      let captures = 0;
      let checks = 0;
      let moves = this.engine.moves({ verbose: true });
      if (color !== this.currentColor()) {
        moves = this.opponentMoves();
      }

      if (moves.length === 0) {
        return null; // It´s an invalid position
      }

      moves.forEach(function (move) {
        if (move["captured"]) {
          captures++;
        }
        if (move["san"].includes("+")) {
          checks++;
        }
      });

      const movesFromTo = moves.map((x) => x.from + x.to);

      // promotions count as 4 moves. This remove those duplicates moves.
      const legalMoves = movesFromTo.filter(function (elem, index, self) {
        return index === self.indexOf(elem);
      });

      threats[`legal_${color}`] = legalMoves.length;
      threats[`checks_${color}`] = checks;
      threats[`threat_${color}`] = captures;
      threats["turn"] = color;
      return threats;
    },

    paintThreats() {
      const moves = this.engine.moves({ verbose: true });
      const threats = [];
      moves.forEach(function (move) {
        threats.push({ orig: move.to, brush: "yellow" });

        if (move["captured"]) {
          threats.push({ orig: move.from, dest: move.to, brush: "red" });
        }
        if (move["san"].includes("+")) {
          threats.push({ orig: move.from, dest: move.to, brush: "blue" });
        }
      });
      this.ui.setShapes(threats);
    },

    clearThreats() {
      this.ui.setShapes([]);
    },

    makeMove(orig, dest, promotion) {
      this.calculatePromotions();

      if (this.isPromotion(orig, dest)) {
        if (promotion) {
          // if specify a promotion, then use it
          this.engine.move({ from: orig, to: dest, promotion: promotion });
        } else {
          if (this.autoQueen) {
            this.engine.move({ from: orig, to: dest, promotion: "q" });
          } else {
            // otherwise, let user decide
            this.promote(orig, dest);
            return;
          }
        }
      } else {
        this.engine.move({ from: orig, to: dest });
      }

      this.afterMove(orig, dest);
    },

    afterMove(orig, dest) {
      // fires the after move event after each move with move's data
      // this is an after move event, so color of this move is previous color
      const moveColor =
        this.currentColor() === CHESS_SIDE.WHITE
          ? CHESS_SIDE.BLACK
          : CHESS_SIDE.WHITE;
      const moves = this.engine.history();
      const lastMove = moves.at(-1);
      const isGameOver = this.isGameOver();

      const moveData = {
        orig,
        dest,
        moveNumber: moves.length,
        // who made this move
        color: moveColor,
        // who made the next move
        nextColor: this.currentColor(),
        fen: this.currentFen(),
        pgn: this.currentPgn(),
        move: lastMove,
        isPremove: !!this.premoveData,
        isGameOver: isGameOver,
      };

      this.$emit("onAfterMove", moveData);
    },

    promote(orig, dest) {
      // show promotion modal
      const promotingColor = this.currentColor();
      this.promoteModal.showModal(orig, dest, promotingColor);
    },

    onPromotion(orig, dest, promotion) {
      this.makeMove(orig, dest, promotion);
    },

    playSound() {
      if (!this.audio) {
        return;
      }

      if (this.isGameOver()) {
        playAudio("end");
        return;
      }

      const moves = this.engine.history({ verbose: true });
      const lastMove = moves.at(-1);

      if (!lastMove) {
        return;
      }

      if (lastMove.captured) {
        playAudio("capture");
      } else {
        playAudio("move");
      }
    },

    updateGameUi() {
      // refresh the temporary ui to show the temporary pgn
      const moves = this.engine.history({ verbose: true });
      const lastMove = moves.at(-1);

      // a map of square to an array containing all possible next moves of this square (cg.Dests)
      const possibleDestsOfAllSquares = {};
      this.engine.SQUARES.forEach((s) => {
        const ms = this.engine.moves({ square: s, verbose: true });
        if (ms.length) possibleDestsOfAllSquares[s] = ms.map((m) => m.to);
      });
      const possibleNextMoves = this.engine.moves({ verbose: true });

      // update game ui
      // highlight.lastMove = true is only effective when lastMove is set,
      // so set it to the latest move after every move
      // highlight.check = true is only effective when check is set,
      // so set it after every move
      this.ui.set({
        turnColor:
          this.engine.turn() === "w" ? CHESS_SIDE.WHITE : CHESS_SIDE.BLACK,
        fen: this.engine.fen(),
        lastMove: lastMove ? [lastMove.from, lastMove.to] : [],
        check: this.engine.in_check(),
        movable: {
          color: this.movable,
          dests: possibleDestsOfAllSquares,
        },
        // an array of all possible next moves of all squares
        premovable: { dests: possibleNextMoves },
        orientation: this.orientation,
      });

      // show possible threats when game isn't over
      if (this.showThreats) {
        this.paintThreats();
      }

      // if premove is made, play it
      if (this.premoveData) {
        this.ui.playPremove();
      }
    },

    isPromotion(orig, dest) {
      const filteredPromotions = this.promotions.filter(
        (move) => move.from === orig && move.to === dest
      );
      return filteredPromotions.length > 0; // The current movement is a promotion
    },

    opponentMoves() {
      let tokens = this.currentFen().split(" ");
      tokens[1] = tokens[1] === "w" ? "b" : "w";
      tokens = tokens.join(" ");
      const engine = new Chess();
      const valid = engine.load(tokens);
      if (valid) {
        return engine.moves({ verbose: true });
      } else {
        return [];
      }
    },

    isGameOver() {
      // TODO: upgrade to newer chess.js version to use camelCase methods
      return this.engine.game_over();
    },

    currentColor() {
      return this.engine.turn() === "w" ? CHESS_SIDE.WHITE : CHESS_SIDE.BLACK;
    },

    turnColor() {
      return this.currentColor === CHESS_SIDE.BLACK
        ? CHESS_SIDE.WHITE
        : CHESS_SIDE.BLACK;
    },

    currentPgn() {
      return this.engine.pgn();
    },

    currentFen() {
      return this.engine.fen();
    },
  },

  mounted() {
    this.ui = Chessground(this.$refs.board, this.config);
    this.updateGameUi(this.engine);

    this.promoteModal = this.$refs.promoteModal;

    // To support relative board size with 100%
    // without this, the board pieces is broken on resize
    // when using relative size like width: 100%, height: 100%
    window.addEventListener("resize", _.debounce(this.triggerRerender, 100));
    window.addEventListener(
      "scroll",
      _.debounce(this.triggerRerender, 100),
      true
    );

    // because chessboard is relative to parent,
    // rerender the chessboard right after parent size is available,
    // otherwise chessboard is rendered with invalid size
    // causing all the pieces to be placed within a single square
    setTimeout(() => {
      this.triggerRerender();
    }, 500);

    this.loadPosition();
  },

  created() {
    this.promotions = [];
    this.engine = new Chess();
    this.tmpEngine = new Chess();
  },

  destroyed() {
    window.removeEventListener("resize", this.triggerRerender);
    window.removeEventListener("scroll", this.triggerRerender);
  },
};
</script>

<style>
@import "../../assets/styles/chess-board.css";
</style>

<style scoped>
@import "../../assets/styles/chess-board-coords-default.css";

.chessboard__container {
  aspect-ratio: 1;
  width: 100%;
  height: 100%;
}
.blue.merida {
  width: 100%;
  height: 100%;
}
.cg-board-wrap {
  width: 100%;
  height: 100%;
}
</style>

<style scoped>
/*
 * Chessground base css properties with customization.
 *
 * You need to include the css files in themes folder in order to have the
 * board and pieces displayed!
 */

.cg-board-wrap {
  position: relative;
  display: block;
}
.cg-board {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  line-height: 0;
  background-size: cover;
  cursor: pointer;
}
.cg-board square {
  position: absolute;
  top: 0;
  left: 0;
  width: 12.5%;
  height: 12.5%;
}
.cg-board square.move-dest {
  background: radial-gradient(
    rgba(20, 85, 30, 0.5) 22%,
    #208530 0,
    rgba(0, 0, 0, 0.3) 0,
    rgba(0, 0, 0, 0) 0
  );
}
.cg-board square.premove-dest {
  background: radial-gradient(
    rgba(20, 30, 85, 0.5) 22%,
    #203085 0,
    rgba(0, 0, 0, 0.3) 0,
    rgba(0, 0, 0, 0) 0
  );
}
.cg-board square.oc.move-dest {
  background: radial-gradient(
    transparent 0%,
    transparent 80%,
    rgba(20, 85, 0, 0.3) 80%
  );
}
.cg-board square.oc.premove-dest {
  background: radial-gradient(
    transparent 0%,
    transparent 80%,
    rgba(20, 30, 85, 0.2) 80%
  );
}
.cg-board .cg-square.move-dest.drag-over,
.cg-board .cg-square.premove-dest.drag-over {
  box-shadow: inset 0 0 10px 2px rgba(216, 85, 0, 0.9);
}
.cg-board square.last-move {
  will-change: transform;
  background-color: rgba(155, 199, 0, 0.41);
}
.cg-board square.selected {
  background-color: rgba(20, 85, 30, 0.5);
}
.cg-board square.check {
  background: radial-gradient(
    ellipse at center,
    rgba(255, 0, 0, 1) 0%,
    rgba(231, 0, 0, 1) 25%,
    rgba(169, 0, 0, 0) 89%,
    rgba(158, 0, 0, 0) 100%
  );
}
.cg-board square.current-premove {
  background-color: rgba(20, 30, 85, 0.5);
}
.cg-board piece {
  position: absolute;
  top: 0;
  left: 0;
  width: 12.5%;
  height: 12.5%;
  background-size: cover;
  z-index: 2;
  will-change: transform;
}
.cg-board piece.dragging {
  cursor: move;
  z-index: 9;
}
.cg-board piece.anim {
  z-index: 8;
}
.cg-board piece.fading {
  z-index: 1;
  opacity: 0.5;
}
.cg-board-wrap div.over {
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(20, 85, 30, 0.3);
}
.cg-board-wrap piece.ghost {
  opacity: 0.3;
}
.cg-board-wrap svg {
  overflow: hidden;
  position: relative;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  opacity: 0.6;
}
.cg-board-wrap svg image {
  opacity: 0.5;
}
.cg-board-wrap coords {
  position: absolute;
  display: flex;
  pointer-events: none;
  opacity: 0.8;
  font-size: 9px;
}
.cg-board-wrap coords.ranks {
  right: -15px;
  top: 0;
  flex-flow: column-reverse;
  height: 100%;
  width: 12px;
}
.cg-board-wrap coords.ranks.black {
  flex-flow: column;
}
.cg-board-wrap coords.files {
  bottom: -16px;
  left: 0;
  flex-flow: row;
  width: 100%;
  height: 16px;
  text-transform: uppercase;
  text-align: center;
}
.cg-board-wrap coords.files.black {
  flex-flow: row-reverse;
}
.cg-board-wrap coords coord {
  flex: 1 1 auto;
}
.cg-board-wrap coords.ranks coord {
  transform: translateY(39%);
}
</style>
